import {
  EngagementMilestoneSchema,
  EngagementPlanResponseSchema,
  EngagementStakeholderSchema,
  EngagementSummarySchema,
  PortalBriefPatchSchema,
  PortalBriefResponseSchema,
  PortalInboxItemSchema,
  PortalInboxListResponseSchema,
  PortalInboxReviewSchema,
  PortalMessageCreateSchema,
  PortalMessageListResponseSchema,
  PortalObjectGrantCandidateListResponseSchema,
  PortalObjectGrantCreateSchema,
  PortalObjectGrantListResponseSchema,
  PortalObjectGrantSchema,
  PortalThreadListResponseSchema,
  type EngagementMilestone,
  type EngagementMilestoneCreate,
  type EngagementMilestoneUpdate,
  type EngagementStakeholder,
  type EngagementStakeholderCreate,
  type EngagementStakeholderUpdate,
  type PortalBriefPatch,
  type PortalBriefResponse,
  type PortalInboxItem,
  type PortalInboxReview,
  type PortalMessage,
  type PortalMessageCreate,
  type PortalObjectGrantCandidate,
  type PortalObjectGrant,
  type PortalObjectGrantCreate,
  type PortalObjectKind,
} from "@l30/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch, apiFetchUnchecked } from "./client.js";

/**
 * Engagement plan (Sprint B 7b') hooks for the staff app.
 *
 * v1: every staff hook scopes to the partner's *default* engagement
 * (the one bootstrapped by provisionTenant). The tenant context is
 * carried by the existing `tenantOverride` arg the partner switcher
 * pipes through `apiFetch`. When multi-engagement support lands the
 * default-engagement helper goes away and the page passes an explicit
 * engagementId.
 */

const PLAN_KEY = ["engagement-plan"] as const;
const PORTAL_OBJECT_GRANTS_KEY = [...PLAN_KEY, "object-grants"] as const;

/** Resolve the default engagement for the active partner. */
export function useDefaultEngagement(tenantSlug: string | null) {
  return useQuery({
    queryKey: [...PLAN_KEY, "default", tenantSlug],
    enabled: !!tenantSlug,
    queryFn: async () => {
      const engagement = await apiFetch(
        "/portal-admin/engagements/default",
        { tenantOverride: tenantSlug! },
        EngagementSummarySchema,
      );
      return { tenantSlug: tenantSlug!, engagement };
    },
  });
}

/** Single-shot read: engagement + stakeholders + milestones. */
export function useEngagementPlan(
  tenantSlug: string | null,
  engagementId: string | null,
  includeArchived = false,
) {
  const qs = includeArchived ? "?includeArchived=true" : "";
  return useQuery({
    queryKey: [...PLAN_KEY, tenantSlug, engagementId, { includeArchived }],
    enabled: !!tenantSlug && !!engagementId,
    queryFn: () =>
      apiFetch(
        `/portal-admin/engagements/${engagementId}/plan${qs}`,
        { tenantOverride: tenantSlug! },
        EngagementPlanResponseSchema,
      ),
  });
}

// ─── Stakeholders ──────────────────────────────────────────

export function useCreateStakeholder(tenantSlug: string | null, engagementId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: EngagementStakeholderCreate) => {
      if (!tenantSlug || !engagementId) {
        throw new Error("Tenant + engagement required");
      }
      return apiFetch<EngagementStakeholder>(
        `/portal-admin/engagements/${engagementId}/stakeholders`,
        {
          method: "POST",
          body: JSON.stringify(data),
          tenantOverride: tenantSlug,
        },
        EngagementStakeholderSchema,
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PLAN_KEY }),
  });
}

export function useUpdateStakeholder(tenantSlug: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: EngagementStakeholderUpdate }) => {
      if (!tenantSlug) throw new Error("Tenant required");
      return apiFetch<EngagementStakeholder>(
        `/portal-admin/stakeholders/${id}`,
        {
          method: "PATCH",
          body: JSON.stringify(data),
          tenantOverride: tenantSlug,
        },
        EngagementStakeholderSchema,
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PLAN_KEY }),
  });
}

export function useArchiveStakeholder(tenantSlug: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (!tenantSlug) throw new Error("Tenant required");
      return apiFetch<EngagementStakeholder>(
        `/portal-admin/stakeholders/${id}/archive`,
        { method: "POST", tenantOverride: tenantSlug },
        EngagementStakeholderSchema,
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PLAN_KEY }),
  });
}

export function useUnarchiveStakeholder(tenantSlug: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (!tenantSlug) throw new Error("Tenant required");
      return apiFetch<EngagementStakeholder>(
        `/portal-admin/stakeholders/${id}/unarchive`,
        { method: "POST", tenantOverride: tenantSlug },
        EngagementStakeholderSchema,
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PLAN_KEY }),
  });
}

export function useDeleteStakeholder(tenantSlug: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (!tenantSlug) throw new Error("Tenant required");
      return apiFetchUnchecked<{ deleted: true; id: string }>(`/portal-admin/stakeholders/${id}`, {
        method: "DELETE",
        tenantOverride: tenantSlug,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PLAN_KEY }),
  });
}

// ─── Milestones ────────────────────────────────────────────

export function useCreateMilestone(tenantSlug: string | null, engagementId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: EngagementMilestoneCreate) => {
      if (!tenantSlug || !engagementId) {
        throw new Error("Tenant + engagement required");
      }
      return apiFetch<EngagementMilestone>(
        `/portal-admin/engagements/${engagementId}/milestones`,
        {
          method: "POST",
          body: JSON.stringify(data),
          tenantOverride: tenantSlug,
        },
        EngagementMilestoneSchema,
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PLAN_KEY }),
  });
}

export function useUpdateMilestone(tenantSlug: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: EngagementMilestoneUpdate }) => {
      if (!tenantSlug) throw new Error("Tenant required");
      return apiFetch<EngagementMilestone>(
        `/portal-admin/milestones/${id}`,
        {
          method: "PATCH",
          body: JSON.stringify(data),
          tenantOverride: tenantSlug,
        },
        EngagementMilestoneSchema,
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PLAN_KEY }),
  });
}

export function useArchiveMilestone(tenantSlug: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (!tenantSlug) throw new Error("Tenant required");
      return apiFetch<EngagementMilestone>(
        `/portal-admin/milestones/${id}/archive`,
        { method: "POST", tenantOverride: tenantSlug },
        EngagementMilestoneSchema,
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PLAN_KEY }),
  });
}

export function useUnarchiveMilestone(tenantSlug: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (!tenantSlug) throw new Error("Tenant required");
      return apiFetch<EngagementMilestone>(
        `/portal-admin/milestones/${id}/unarchive`,
        { method: "POST", tenantOverride: tenantSlug },
        EngagementMilestoneSchema,
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PLAN_KEY }),
  });
}

export function useDeleteMilestone(tenantSlug: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (!tenantSlug) throw new Error("Tenant required");
      return apiFetchUnchecked<{ deleted: true; id: string }>(`/portal-admin/milestones/${id}`, {
        method: "DELETE",
        tenantOverride: tenantSlug,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PLAN_KEY }),
  });
}

// ─── Executive brief workflow ──────────────────────────────

export function usePortalBrief(tenantSlug: string | null, engagementId: string | null) {
  return useQuery({
    queryKey: [...PLAN_KEY, "brief", tenantSlug, engagementId],
    enabled: !!tenantSlug && !!engagementId,
    queryFn: () =>
      apiFetch(
        `/portal-admin/engagements/${engagementId}/brief`,
        { tenantOverride: tenantSlug! },
        PortalBriefResponseSchema,
      ),
  });
}

export function useUpdatePortalBrief(tenantSlug: string | null, engagementId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: PortalBriefPatch) => {
      if (!tenantSlug || !engagementId) throw new Error("Tenant + engagement required");
      const parsed = PortalBriefPatchSchema.parse(data);
      return apiFetch<PortalBriefResponse>(
        `/portal-admin/engagements/${engagementId}/brief`,
        {
          method: "PATCH",
          body: JSON.stringify(parsed),
          tenantOverride: tenantSlug,
        },
        PortalBriefResponseSchema,
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PLAN_KEY }),
  });
}

export function useRegeneratePortalBrief(tenantSlug: string | null, engagementId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => {
      if (!tenantSlug || !engagementId) throw new Error("Tenant + engagement required");
      return apiFetch<PortalBriefResponse>(
        `/portal-admin/engagements/${engagementId}/brief/regenerate`,
        { method: "POST", tenantOverride: tenantSlug },
        PortalBriefResponseSchema,
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PLAN_KEY }),
  });
}

export function useSubmitPortalBriefForReview(
  tenantSlug: string | null,
  engagementId: string | null,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => {
      if (!tenantSlug || !engagementId) throw new Error("Tenant + engagement required");
      return apiFetch<PortalBriefResponse>(
        `/portal-admin/engagements/${engagementId}/brief/submit-review`,
        { method: "POST", tenantOverride: tenantSlug },
        PortalBriefResponseSchema,
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PLAN_KEY }),
  });
}

export function usePublishPortalBrief(tenantSlug: string | null, engagementId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => {
      if (!tenantSlug || !engagementId) throw new Error("Tenant + engagement required");
      return apiFetch<PortalBriefResponse>(
        `/portal-admin/engagements/${engagementId}/brief/publish`,
        { method: "POST", tenantOverride: tenantSlug },
        PortalBriefResponseSchema,
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PLAN_KEY }),
  });
}

// ─── Staff portal threads + inbox ──────────────────────────

export function usePortalThreads(tenantSlug: string | null, engagementId: string | null) {
  return useQuery({
    queryKey: [...PLAN_KEY, "threads", tenantSlug, engagementId],
    enabled: !!tenantSlug && !!engagementId,
    queryFn: () =>
      apiFetch(
        `/portal-admin/engagements/${engagementId}/threads`,
        { tenantOverride: tenantSlug! },
        PortalThreadListResponseSchema,
      ),
  });
}

export function usePortalThreadMessages(tenantSlug: string | null, threadId: string | null) {
  return useQuery({
    queryKey: [...PLAN_KEY, "messages", tenantSlug, threadId],
    enabled: !!tenantSlug && !!threadId,
    queryFn: () =>
      apiFetch(
        `/portal-admin/threads/${threadId}/messages`,
        { tenantOverride: tenantSlug! },
        PortalMessageListResponseSchema,
      ),
  });
}

export function useCreateStaffPortalMessage(tenantSlug: string | null, threadId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: PortalMessageCreate) => {
      if (!tenantSlug || !threadId) throw new Error("Tenant + thread required");
      const parsed = PortalMessageCreateSchema.parse(data);
      return apiFetchUnchecked<PortalMessage>(`/portal-admin/threads/${threadId}/messages`, {
        method: "POST",
        body: JSON.stringify(parsed),
        tenantOverride: tenantSlug,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PLAN_KEY }),
  });
}

export function usePortalInbox(tenantSlug: string | null, engagementId: string | null) {
  return useQuery({
    queryKey: [...PLAN_KEY, "inbox", tenantSlug, engagementId],
    enabled: !!tenantSlug && !!engagementId,
    queryFn: () =>
      apiFetch(
        `/portal-admin/engagements/${engagementId}/inbox`,
        { tenantOverride: tenantSlug! },
        PortalInboxListResponseSchema,
      ),
  });
}

export function useReviewPortalInboxItem(tenantSlug: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PortalInboxReview }) => {
      if (!tenantSlug) throw new Error("Tenant required");
      const parsed = PortalInboxReviewSchema.parse(data);
      return apiFetch<PortalInboxItem>(
        `/portal-admin/inbox/${id}/review`,
        {
          method: "POST",
          body: JSON.stringify(parsed),
          tenantOverride: tenantSlug,
        },
        PortalInboxItemSchema,
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PLAN_KEY }),
  });
}

// ─── Portal object publication ─────────────────────────────

export function usePortalObjectGrants(
  tenantSlug: string | null,
  engagementId: string | null,
  objectKind?: PortalObjectKind,
) {
  const params = new URLSearchParams();
  if (objectKind) params.set("objectKind", objectKind);
  const qs = params.toString();
  return useQuery({
    queryKey: [...PORTAL_OBJECT_GRANTS_KEY, tenantSlug, engagementId, objectKind ?? null],
    enabled: !!tenantSlug && !!engagementId,
    queryFn: () =>
      apiFetch(
        `/portal-admin/engagements/${engagementId}/grants${qs ? `?${qs}` : ""}`,
        { tenantOverride: tenantSlug! },
        PortalObjectGrantListResponseSchema,
      ),
  });
}

export function usePortalObjectGrantCandidates(
  tenantSlug: string | null,
  engagementId: string | null,
  objectKind: PortalObjectKind,
) {
  const params = new URLSearchParams({ objectKind });
  return useQuery<{ candidates: PortalObjectGrantCandidate[] }>({
    queryKey: [...PORTAL_OBJECT_GRANTS_KEY, "candidates", tenantSlug, engagementId, objectKind],
    enabled: !!tenantSlug && !!engagementId,
    queryFn: () =>
      apiFetch(
        `/portal-admin/engagements/${engagementId}/grant-candidates?${params.toString()}`,
        { tenantOverride: tenantSlug! },
        PortalObjectGrantCandidateListResponseSchema,
      ),
  });
}

export function useGrantPortalObject(tenantSlug: string | null, engagementId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: PortalObjectGrantCreate) => {
      if (!tenantSlug || !engagementId) throw new Error("Tenant + engagement required");
      const parsed = PortalObjectGrantCreateSchema.parse(data);
      return apiFetch<PortalObjectGrant>(
        `/portal-admin/engagements/${engagementId}/grants`,
        {
          method: "POST",
          body: JSON.stringify(parsed),
          tenantOverride: tenantSlug,
        },
        PortalObjectGrantSchema,
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PORTAL_OBJECT_GRANTS_KEY }),
  });
}

export function useRevokePortalObjectGrant(tenantSlug: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (!tenantSlug) throw new Error("Tenant required");
      return apiFetch<PortalObjectGrant>(
        `/portal-admin/grants/${id}/revoke`,
        { method: "POST", tenantOverride: tenantSlug },
        PortalObjectGrantSchema,
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PORTAL_OBJECT_GRANTS_KEY }),
  });
}
